import { ElementType, Suspense, lazy, useEffect, useState } from 'react'
import { RouteObject, useRoutes, useSearchParams } from 'react-router-dom'
import AmeSpin from './components/AmeSpin/AmeSpin'
import { PAGE_LINKS } from './helpers/commonConsts'
import { userService } from './services/userService'
import { routerService } from './services/routerService'
import { autorun } from 'mobx'
import { commonUtils } from './helpers/commonUtils'

const Loadable = (Component: ElementType) => (props: any) =>
  (
    <Suspense fallback={<AmeSpin fullscreen={true} />}>
      <Component {...props} />
    </Suspense>
  )

const HomePage = Loadable(lazy(() => import('./pages/HomePage/HomePage')))
const LoginPage = Loadable(lazy(() => import('./pages/LoginPage/LoginPage')))
const ReviewPage = Loadable(lazy(() => import('./pages/HomePage/children/ReviewPage/ReviewPage')))
const MaterialLibraryPage = Loadable(
  lazy(() => import('./pages/HomePage/children/MaterialLibraryPage/MaterialLibraryPage'))
)
const TeamManagementPage = Loadable(
  lazy(() => import('./pages/TeamManagementPage/TeamManagementPage'))
)
const TagManagePage = Loadable(lazy(() => import('./pages/TagManagePage/TagManagePage')))
const RedirectPage = Loadable(lazy(() => import('./pages/RedirectPage/RedirectPage')))
const VisitorPage = Loadable(lazy(() => import('./pages/VisitorPage/VisitorPage')))
const WorkspaceManagePage = Loadable(
  lazy(() => import('./pages/WorkspaceManagePage/WorkspaceManagePage'))
)

type RouteObjectWithMeta = Omit<RouteObject, 'children'> & {
  auth?: boolean
  children?: RouteObjectWithMeta[]
}

const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const [canRender, setRenderFlag] = useState(false)
  const [searchParams] = useSearchParams({ w_id: '' })

  useEffect(() => {
    const w_id = searchParams.get('w_id')
    if (w_id) {
      if (!userService.isLogin) {
        //此时是分享页面，需要生成一个临时用户
        userService.login({
          anonymous: commonUtils.genId(),
          workspaceId: w_id || undefined,
        })
      }
    } else {
      if (!userService.isLogin) {
        routerService.navigateTo('login', {
          redirectUrl: encodeURIComponent(window.location.href),
        })
        return
      }
    }

    autorun(() => {
      if (userService.workspaceId) {
        if (w_id && userService.isVisitor && userService.workspaceId.toString() !== w_id) {
          //此时是分享页面，需要生成一个临时用户
          userService.login({
            anonymous: commonUtils.genId(),
            workspaceId: w_id || undefined,
          })
          return
        }
        setRenderFlag(true)
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return canRender ? children : null
}

const checkAuth = (routeList: RouteObjectWithMeta[]) => {
  return routeList.map((route) => {
    if (route.children) {
      route.children = checkAuth(route.children)
    }
    if (route?.auth) {
      route.element = <AuthRoute>{route.element}</AuthRoute>
    }
    return route
  })
}

const routes: Array<RouteObjectWithMeta> = [
  {
    path: PAGE_LINKS.home,
    element: <HomePage />,
    children: [
      {
        path: PAGE_LINKS.review,
        element: <ReviewPage />,
        auth: true,
      },
      {
        path: PAGE_LINKS.materialLibrary,
        element: <MaterialLibraryPage />,
        auth: true,
      },
    ],
  },
  {
    path: PAGE_LINKS.teamManage,
    element: <TeamManagementPage />,
    auth: true,
  },
  {
    path: PAGE_LINKS.tagManage,
    element: <TagManagePage />,
    auth: true,
  },
  {
    path: PAGE_LINKS.workspaceManage,
    element: <WorkspaceManagePage />,
    auth: true,
  },
  {
    path: PAGE_LINKS.login,
    element: <LoginPage />,
  },
  {
    path: PAGE_LINKS.redirect,
    element: <RedirectPage />,
  },
  {
    path: PAGE_LINKS.visitor,
    element: <VisitorPage />,
  },
]

export const Router = () => {
  return useRoutes(checkAuth(routes) as RouteObject[])
}
